import { put, call, takeLatest, type CallEffect, type PutEffect, type ForkEffect } from 'redux-saga/effects';

import { type ImagenAction } from './interfaces';
import { ImagenActionType } from './enums';
import { uploadDocument, activateDocument, generateImage } from '../aimagine/api';
import { AImagineTool } from '../aimagine/enums';
import { uploadFile } from '@utilities';
import { toast } from '@utilities/toasts';

type ImagenSagaEffect = Generator<CallEffect<any> | PutEffect<ImagenAction>>;
type ImagenSagaForkEffect = Generator<ForkEffect<void>>;

/**
 * Convert a base64 data URL (e.g. "data:image/png;base64,XXXX") into a File
 * we can hand off to the document upload pipeline.
 */
const dataURLToFile = (dataURL: string, fileName: string): File => {
	const [header, base64Data] = dataURL.split(',');
	const mimeMatch = header.match(/data:(.*?);base64/);
	const mimeType = mimeMatch?.[1] || 'image/png';

	const binaryString = atob(base64Data);
	const bytes = new Uint8Array(binaryString.length);
	for (let i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i);
	}

	return new File([bytes], fileName, { type: mimeType });
};

export function* generateRenderEffect(action: ImagenAction): ImagenSagaEffect {
	try {
		const { imageData, prompt, area } = action.payload;

		if (!imageData) {
			yield put({
				type: ImagenActionType.GENERATE_RENDER_FAILURE,
				payload: { error: 'No image data provided' }
			});
			toast('ERROR', 'Failed to capture screenshot');
			return;
		}

		// 1) Wrap the base64 screenshot into a File so we can push it through
		//    the same upload pipeline AImagine uses.
		const fileName = `threed-render-${Date.now()}.png`;
		const file = dataURLToFile(imageData, fileName);

		// 2) Request a pre-signed upload URL from the backend.
		const uploadRequest = {
			reference_id: fileName,
			reference_type: 'USER',
			documents: [
				{
					category: 'INSPIRATION_IMAGES',
					sub_category: 'INSPIRATION_AIGENERATED',
					attachments: [{ file: fileName, cover_image: true }],
					document_id: 0
				}
			]
		};

		const { data: uploadData }: any = yield call(uploadDocument, uploadRequest);

		const responses = uploadData?.documents_upload_responses ?? uploadData?.documentsUploadResponses;
		if (!responses?.length) {
			throw new Error('Upload did not return document');
		}

		const docResponse = responses[0];
		const documentId: number | undefined = docResponse?.document_id ?? docResponse?.documentId;
		const attachments = docResponse?.attachments;
		if (documentId == null || !attachments?.length) {
			throw new Error('Invalid upload response');
		}

		const firstAttachment = attachments[0];
		const uploadUrl = firstAttachment?.document_url ?? firstAttachment?.documentUrl;
		const attachmentFileName = firstAttachment?.file_name ?? firstAttachment?.fileName ?? fileName;
		if (!uploadUrl) {
			throw new Error('Upload response missing document_url');
		}

		// 3) PUT the file bytes to S3, then activate the document.
		yield call(uploadFile, uploadUrl, file, attachmentFileName);
		yield call(activateDocument, { documentIds: [documentId] });

		// 4) Call the same /aimagine/generate endpoint AImagine uses, with the
		//    area resolved upstream by the caller (defaults to "GLOBAL").
		const generateRequest = {
			tool: AImagineTool.RESTYLE,
			area: area || 'GLOBAL',
			documentId,
			options: {},
			...(prompt ? { prompt } : {})
		};

		const { data: responseData }: any = yield call(generateImage, generateRequest);

		if (!responseData?.success) {
			const errorMessage = responseData?.errorMessage || 'Image generation failed';
			yield put({
				type: ImagenActionType.GENERATE_RENDER_FAILURE,
				payload: { error: errorMessage }
			});
			toast('ERROR', errorMessage);
			return;
		}

		const generatedUrl = responseData?.imageUrl ?? responseData?.image_url;
		if (!generatedUrl) {
			throw new Error('Generate response missing image URL');
		}

		yield put({
			type: ImagenActionType.GENERATE_RENDER_SUCCESS,
			payload: { imageData: generatedUrl }
		});
		toast('SUCCESS', 'Photorealistic render generated successfully!');
	} catch (err: any) {
		const errorMessage = err?.message || 'An unexpected error occurred';
		yield put({
			type: ImagenActionType.GENERATE_RENDER_FAILURE,
			payload: { error: errorMessage }
		});
		toast('ERROR', errorMessage);
	}
}

export function* generateRenderSaga(): ImagenSagaForkEffect {
	yield takeLatest(ImagenActionType.GENERATE_RENDER_REQUEST, generateRenderEffect);
}
